import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { CheckIcon, ChevronDownIcon } from './icons.tsx';

/**
 * Replacement for a native `<select>`.
 *
 * The closed control can be styled, but the popup a browser opens is drawn by
 * the OS and ignores the page entirely — which is why the stock one looks
 * nothing like the rest of the app. This renders the list itself.
 *
 * The list goes through a portal with fixed positioning: several of the cards
 * it appears inside are `overflow-hidden` for their rounded corners, and an
 * absolutely positioned popup would be clipped by them.
 */

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
  description?: string;
}

interface SelectProps<T extends string | number> {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function Select<T extends string | number>({
  value,
  options,
  onChange,
  id,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: SelectProps<T>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; above: boolean }>({
    top: 0,
    left: 0,
    width: 0,
    above: false,
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const place = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const box = button.getBoundingClientRect();
    const estimated = Math.min(options.length * 40 + 8, 280);
    const spaceBelow = window.innerHeight - box.bottom;

    // Flip upward when the list would otherwise run off the bottom.
    const above = spaceBelow < estimated && box.top > spaceBelow;

    setRect({
      top: above ? box.top - 4 : box.bottom + 4,
      left: box.left,
      width: box.width,
      above,
    });
  }, [options.length]);

  useLayoutEffect(() => {
    if (!open) return;

    place();
    setHighlighted(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, place, selectedIndex]);

  // Fixed positioning is relative to the viewport, so any scroll behind the
  // popup would leave it stranded next to nothing.
  useEffect(() => {
    if (!open) return;

    const update = (): void => {
      place();
    };

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;

      setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const commit = (index: number): void => {
    const option = options[index];
    if (!option) return;

    onChange(option.value);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (disabled) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setHighlighted((current) => (current + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlighted((current) => (current - 1 + options.length) % options.length);
        break;
      case 'Home':
        event.preventDefault();
        setHighlighted(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlighted(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(highlighted);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          setOpen((current) => !current);
        }}
        onKeyDown={onKeyDown}
        className={clsx(
          'lg-input flex items-center justify-between gap-2 text-left',
          open && 'border-brand-500 ring-2 ring-brand-500/20',
          className,
        )}
      >
        <span className={clsx('truncate', !selected && 'text-slate-400')}>
          {selected?.label ?? 'Select…'}
        </span>
        <ChevronDownIcon
          className={clsx(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              tabIndex={-1}
              aria-activedescendant={`${listId}-${String(highlighted)}`}
              style={{
                position: 'fixed',
                top: rect.above ? undefined : rect.top,
                bottom: rect.above ? window.innerHeight - rect.top : undefined,
                left: rect.left,
                width: rect.width,
              }}
              className="lg-scroll z-[110] max-h-70 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
            >
              {options.map((option, index) => {
                const isSelected = option.value === value;

                return (
                  <li
                    key={String(option.value)}
                    id={`${listId}-${String(index)}`}
                    role="option"
                    aria-selected={isSelected}
                    onPointerEnter={() => {
                      setHighlighted(index);
                    }}
                    onClick={() => {
                      commit(index);
                    }}
                    className={clsx(
                      'flex cursor-pointer items-start gap-2 px-3 py-2 text-sm',
                      index === highlighted
                        ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/50 dark:text-brand-100'
                        : 'text-slate-700 dark:text-slate-200',
                    )}
                  >
                    <CheckIcon
                      className={clsx(
                        'mt-0.5 h-3.5 w-3.5 shrink-0',
                        isSelected ? 'text-brand-600 dark:text-brand-300' : 'invisible',
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block truncate">{option.label}</span>
                      {option.description ? (
                        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null}
    </>
  );
}
